import {
  moduleLoader,
  suspendingModuleRead,
  type ModuleLoader
} from '@Pages/pageRuntime/sdkMemberLoaders';
import { createElement, Suspense, type ComponentProps, type ComponentType } from 'react';

/**
 * A component that loads its module on first render. It always renders the
 * same tree, `Suspense` around one stable inner component, so a parent that
 * re-renders after the load keeps the component's state. The inner component
 * renders at once when the module has loaded or was seeded; `React.lazy`
 * would still suspend once on its first render.
 */
// oxlint-disable-next-line typescript/no-explicit-any -- `never` props reject generic SDK components such as DataTable
export function lazyModuleComponent<TModule, TComponent extends ComponentType<any>>(
  loader: ModuleLoader<TModule>,
  pick: (module: TModule) => TComponent
): TComponent {
  const read = suspendingModuleRead(loader);

  function LoadedComponent(props: ComponentProps<TComponent>) {
    return createElement(pick(read()), props);
  }

  return function LazyComponent(props: ComponentProps<TComponent>) {
    return createElement(Suspense, { fallback: null }, createElement(LoadedComponent, props));
  } as TComponent;
}

/** A lazy component over the default export of a module that only it loads. */
// oxlint-disable-next-line typescript/no-explicit-any -- `never` props reject generic SDK components such as DataTable
export function lazyComponent<TComponent extends ComponentType<any>>(
  load: () => Promise<{ default: TComponent }>
): TComponent {
  return lazyModuleComponent(moduleLoader(load), (module) => module.default);
}
